import { HttpClient } from '@angular/common/http';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../core/auth.service';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';

type Billing = 'monthly' | 'yearly';
type SubscriptionProduct = {
  code: string;
  solutionSlug: string;
  listingID: number;
  slug: string;
  name: string;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  billingPeriods?: string[];
  prices?: Record<string, { optionID?: number; subOptionID?: number }> | [];
  currency: string;
  branchQuantityEnabled?: boolean;
  minBranchCount?: number;
  maxBranchCount?: number;
  trialDays?: number;
  featured?: boolean;
  features?: string[];
};

@Component({
  selector: 'app-aymudo-subscribe-page',
  imports: [RouterLink],
  template: `
    <main class="subscribe-page">
      <section class="subscribe-hero">
        <div>
          <p>AYMUDO ABONELİKLERİ</p>
          <h1>İşine uygun sistemi seç.<br><em>Hemen çalışmaya başla.</em></h1>
          <span>Gerçek abonelik ürünlerini karşılaştır, ödeme dönemini seç ve kurulumunu tek akışta başlat.</span>
        </div>
        <aside>
          @if (auth.loggedIn()) {
            <small>HESABIN HAZIR</small>
            <strong>{{ auth.user()?.name || 'Hoş geldin' }}</strong>
            <span>Seçtiğin paket doğrudan bu hesabına bağlanacak.</span>
            <a [routerLink]="i18n.routePath('subscriptions')">Aboneliklerimi yönet →</a>
          } @else {
            <small>HESABIN YOK MU?</small>
            <strong>Önce paketini seç.</strong>
            <span>Satın alma adımında giriş yapabilir veya hesabını oluşturup aynı paketten devam edebilirsin.</span>
            <a [routerLink]="i18n.routePath('login')">Zaten hesabım var →</a>
          }
        </aside>
      </section>

      <section class="catalog">
        <header>
          <div><p>01 · ÇÖZÜMÜNÜ SEÇ</p><h2>Ne kurmak istiyorsun?</h2></div>
          @if (!loading() && products().length) {
            <span>{{ products().length }} canlı abonelik ürünü</span>
          }
        </header>

        @if (loading()) {
          <div class="state"><i></i><strong>Paketler hazırlanıyor…</strong></div>
        } @else if (error()) {
          <div class="state error"><strong>{{ error() }}</strong><button type="button" (click)="load()">Yeniden dene</button></div>
        } @else {
          <nav class="solutions" aria-label="Çözüm seçimi">
            @for (group of groups(); track group.key) {
              <button type="button" [class.active]="selectedSolution() === group.key" (click)="selectSolution(group.key)">
                <span>{{ group.icon }}</span><b>{{ group.title }}</b><small>{{ group.count }} paket</small>
              </button>
            }
          </nav>

          <div class="plan-toolbar">
            <div><p>02 · PAKETİNİ SEÇ</p><h2>{{ activeGroupTitle() }}</h2></div>
            <div class="billing" role="group" aria-label="Ödeme dönemi">
              <button type="button" [class.active]="billing() === 'monthly'" (click)="billing.set('monthly')">Aylık</button>
              <button type="button" [class.active]="billing() === 'yearly'" (click)="billing.set('yearly')">Yıllık <small>2 ay avantaj</small></button>
            </div>
          </div>

          <div class="plans">
            @for (plan of visibleProducts(); track plan.listingID) {
              <article [class.featured]="plan.featured">
                <div class="plan-heading">
                  <small>{{ plan.featured ? 'ÖNERİLEN' : (plan.trialDays ? plan.trialDays + ' GÜN DENEME' : 'AYMUDO PAKETİ') }}</small>
                  <h3>{{ plan.name }}</h3>
                  <p>{{ plan.description }}</p>
                </div>
                <div class="price">
                  <strong>{{ price(plan) }}</strong>
                  <span>{{ priceSuffix(plan) }}</span>
                </div>
                @if (plan.branchQuantityEnabled) {
                  <label class="branch-count">
                    <span>Şube / kullanım hakkı</span>
                    <span class="stepper"><button type="button" (click)="changeBranches(-1)">−</button><b>{{ branchCount() }}</b><button type="button" (click)="changeBranches(1)">+</button></span>
                  </label>
                }
                <ul>
                  @for (feature of (plan.features || []).slice(0, 7); track feature) { <li><i>✓</i>{{ feature }}</li> }
                </ul>
                <a class="choose" [routerLink]="i18n.routePath('listing',{slug:plan.slug})" [queryParams]="planParams(plan)">
                  <span>{{ auth.loggedIn() ? 'Bu paketle devam et' : 'Paketi seç ve devam et' }}</span><b>→</b>
                </a>
              </article>
            }
          </div>
        }
      </section>

      <section class="flow">
        <header><p>SONRASI NET</p><h2>Paket seçiminden yayına kadar.</h2></header>
        <ol>
          <li><b>01</b><div><strong>Paketini seç</strong><span>Aylık veya yıllık dönemini ve gerekiyorsa şube sayını belirle.</span></div></li>
          <li><b>02</b><div><strong>Güvenli ödemeyi tamamla</strong><span>Oturumun açıksa tekrar giriş yapmazsın; hesabın yoksa kaldığın paketten devam edersin.</span></div></li>
          <li><b>03</b><div><strong>Kurulumunu tamamla</strong><span>Aboneliğin hesabına bağlanır, kısa kurulum bilgileriyle çalışma alanın hazırlanır.</span></div></li>
        </ol>
      </section>
    </main>
  `,
  styles: [`
    :host{display:block}.subscribe-page{color:var(--ink);display:grid;gap:28px;margin:0 auto;max-width:1540px;padding:28px clamp(18px,4vw,68px) 70px}.subscribe-hero{background:#101b17;border-radius:36px;color:#f8f5ed;display:grid;gap:34px;grid-template-columns:minmax(0,1.6fr) minmax(280px,.58fr);overflow:hidden;padding:clamp(34px,6vw,86px);position:relative}.subscribe-hero:after{background:#cfff35;border-radius:50%;content:"";filter:blur(1px);height:310px;opacity:.1;position:absolute;right:-90px;top:-150px;width:310px}.subscribe-hero p,.catalog header p,.plan-toolbar p,.flow header p{color:#9c7860;font-size:.74rem;font-weight:950;letter-spacing:.18em;margin:0;text-transform:uppercase}.subscribe-hero>div>p{color:#cfff35}.subscribe-hero h1{font:650 clamp(3.2rem,7vw,7.5rem)/.86 var(--font-display,Georgia,serif);letter-spacing:-.07em;margin:24px 0}.subscribe-hero h1 em{color:#cfff35;font-style:normal}.subscribe-hero>div>span{color:#bec6c0;display:block;font-size:clamp(1rem,1.4vw,1.25rem);line-height:1.6;max-width:720px}.subscribe-hero aside{align-self:end;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.15);border-radius:24px;display:grid;gap:12px;padding:26px;position:relative;z-index:1}.subscribe-hero aside small{color:#cfff35;font-weight:950;letter-spacing:.14em}.subscribe-hero aside strong{font-size:1.55rem}.subscribe-hero aside span{color:#cbd0cc;line-height:1.5}.subscribe-hero aside a{color:#fff;font-weight:900;margin-top:10px;text-decoration:none}.catalog{background:var(--surface);border:1px solid var(--border);border-radius:36px;padding:clamp(24px,4vw,54px)}.catalog>header,.plan-toolbar{align-items:end;display:flex;gap:20px;justify-content:space-between}.catalog h2,.flow h2,.plan-toolbar h2{font:650 clamp(2.2rem,4.4vw,4.7rem)/.92 var(--font-display,Georgia,serif);letter-spacing:-.055em;margin:12px 0 0}.catalog>header>span{color:var(--muted);font-weight:800}.solutions{display:grid;gap:10px;grid-template-columns:repeat(5,minmax(0,1fr));margin:32px 0 64px}.solutions button{align-items:start;background:transparent;border:1px solid var(--border);border-radius:18px;color:var(--ink);cursor:pointer;display:grid;gap:5px;min-height:112px;padding:17px;text-align:left}.solutions button>span{font-size:1.3rem}.solutions button b{font-size:.94rem}.solutions button small{color:var(--muted)}.solutions button.active{background:var(--ink);border-color:var(--ink);color:var(--paper)}.solutions button.active small{color:color-mix(in srgb,var(--paper) 70%,transparent)}.billing{background:var(--paper);border:1px solid var(--border);border-radius:999px;display:flex;padding:5px}.billing button{background:transparent;border:0;border-radius:999px;color:var(--muted);cursor:pointer;font:inherit;font-weight:900;padding:12px 18px}.billing button small{color:#759600;margin-left:5px}.billing button.active{background:var(--ink);color:var(--paper)}.billing button.active small{color:#cfff35}.plans{display:grid;gap:16px;grid-template-columns:repeat(3,minmax(0,1fr));margin-top:28px}.plans article{background:var(--paper);border:1px solid var(--border);border-radius:26px;display:flex;flex-direction:column;min-height:610px;padding:26px;position:relative}.plans article.featured{border-color:#8caf15;box-shadow:inset 0 5px #cfff35}.plan-heading>small{color:#799800;font-size:.7rem;font-weight:950;letter-spacing:.14em}.plan-heading h3{font:650 2rem/1 var(--font-display,Georgia,serif);letter-spacing:-.04em;margin:14px 0}.plan-heading p{color:var(--muted);line-height:1.55;min-height:72px}.price{align-items:baseline;display:flex;gap:8px;margin:24px 0}.price strong{font-size:2rem;letter-spacing:-.05em}.price span{color:var(--muted);font-size:.82rem}.branch-count{align-items:center;border-bottom:1px solid var(--border);border-top:1px solid var(--border);display:flex;justify-content:space-between;padding:14px 0}.branch-count>span:first-child{font-size:.82rem;font-weight:800}.stepper{align-items:center;display:flex;gap:13px}.stepper button{background:var(--surface);border:1px solid var(--border);border-radius:50%;color:var(--ink);cursor:pointer;height:30px;width:30px}.plans ul{display:grid;gap:10px;list-style:none;margin:22px 0 28px;padding:0}.plans li{display:flex;font-size:.88rem;gap:9px;line-height:1.4}.plans li i{color:#799800;font-style:normal;font-weight:950}.choose{align-items:center;background:var(--ink);border-radius:999px;color:var(--paper);display:flex;font-weight:950;justify-content:space-between;margin-top:auto;padding:16px 18px;text-decoration:none}.featured .choose{background:#cfff35;color:#111}.flow{background:#e8ddd0;border-radius:36px;color:#1c1815;display:grid;gap:38px;grid-template-columns:.8fr 1.2fr;padding:clamp(30px,5vw,68px)}.flow ol{display:grid;gap:0;list-style:none;margin:0;padding:0}.flow li{border-bottom:1px solid rgba(28,24,21,.18);display:grid;gap:20px;grid-template-columns:50px 1fr;padding:22px 0}.flow li>b{color:#79583f}.flow li div{display:grid;gap:6px}.flow li strong{font-size:1.1rem}.flow li span{color:#685d55;line-height:1.5}.state{align-items:center;display:flex;gap:14px;justify-content:center;min-height:260px}.state i{animation:spin .8s linear infinite;border:3px solid var(--border);border-radius:50%;border-top-color:var(--ink);height:28px;width:28px}.state.error{flex-direction:column}.state button{background:var(--ink);border:0;border-radius:999px;color:var(--paper);cursor:pointer;font-weight:900;padding:12px 18px}@keyframes spin{to{transform:rotate(360deg)}}
    .subscribe-hero h1{color:#f8f5ed}
    @media(max-width:1100px){.solutions{grid-template-columns:repeat(3,1fr)}.plans{grid-template-columns:repeat(2,1fr)}.subscribe-hero{grid-template-columns:1fr}.flow{grid-template-columns:1fr}}
    @media(max-width:680px){.subscribe-page{min-width:0;overflow:hidden;padding:14px 12px 50px}.subscribe-page>*,.subscribe-hero>div,.subscribe-hero aside{min-width:0}.subscribe-hero,.catalog,.flow{border-radius:24px;padding:24px 18px}.subscribe-hero h1{font-size:clamp(2.7rem,13.5vw,3.6rem);overflow-wrap:anywhere}.solutions{display:flex;margin:24px -18px 42px;overflow:auto;padding:0 18px;scrollbar-width:none}.solutions button{flex:0 0 150px}.catalog>header,.plan-toolbar{align-items:start;flex-direction:column}.billing{align-self:stretch}.billing button{flex:1;padding:11px 8px}.plans{grid-template-columns:1fr}.plans article{min-height:0}.plan-heading p{min-height:0}.catalog h2,.flow h2,.plan-toolbar h2{font-size:2.25rem;overflow-wrap:anywhere}}
  `]
})
export class AymudoSubscribePage {
  protected readonly auth = inject(AuthService);
  protected readonly i18n = inject(I18nService);
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  protected readonly products = signal<SubscriptionProduct[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal('');
  protected readonly billing = signal<Billing>('monthly');
  protected readonly branchCount = signal(1);
  protected readonly selectedSolution = signal('e-ticaret');
  protected readonly groups = computed(() => {
    const counts = new Map<string, number>();
    for (const product of this.products()) counts.set(product.solutionSlug, (counts.get(product.solutionSlug) || 0) + 1);
    return SOLUTIONS.filter(item => counts.has(item.key)).map(item => ({ ...item, count: counts.get(item.key) || 0 }));
  });
  protected readonly visibleProducts = computed(() => this.products().filter(product => product.solutionSlug === this.selectedSolution()));
  protected readonly activeGroupTitle = computed(() => SOLUTIONS.find(item => item.key === this.selectedSolution())?.title || 'Paketler');

  constructor() {
    const requested = String(this.route.snapshot.queryParamMap.get('solution') || '').trim();
    if (requested) this.selectedSolution.set(requested);
    this.seo.setPage({
      title: 'Aymudo Abonelikleri | Paketini Seç ve Başla',
      description: 'E-ticaret, randevu, QR menü, sağlık, veteriner, sosyal medya ve özel platform aboneliklerini karşılaştır ve başlat.',
      path: '/abone-ol',
      type: 'website',
      brand: 'Aymudo'
    });
    effect(() => { void this.i18n.contentLang(); void this.i18n.country(); void this.load(); });
    void this.auth.refresh();
  }

  protected async load(): Promise<void> {
    this.loading.set(true); this.error.set('');
    try {
      const response = await firstValueFrom(this.http.post<any>('/api/aymudo/core/public-pricing', {
        lang: this.i18n.contentLang(), country: this.i18n.country(), currency: 'TRY'
      }));
      const data = response?.data ?? response ?? {};
      const items = Array.isArray(data.products) ? data.products as SubscriptionProduct[] : [];
      this.products.set(items.filter(item => Number(item.listingID) > 0 && !!item.solutionSlug));
      if (!this.visibleProducts().length && this.groups().length) this.selectedSolution.set(this.groups()[0].key);
    } catch {
      this.error.set('Abonelik paketleri şu anda alınamadı. Lütfen yeniden dene.');
    } finally { this.loading.set(false); }
  }

  protected selectSolution(key: string): void { this.selectedSolution.set(key); }
  protected changeBranches(change: number): void { this.branchCount.set(Math.max(1, Math.min(100, this.branchCount() + change))); }
  protected planParams(plan: SubscriptionProduct): Record<string, string | number> {
    const period = this.period(plan);
    const selectedPrice = !Array.isArray(plan.prices) ? plan.prices?.[period] : undefined;
    const optionID = Number(selectedPrice?.optionID || 0);
    const subOptionID = Number(selectedPrice?.subOptionID || 0);
    const params: Record<string, string | number> = {
      branches: plan.branchQuantityEnabled ? this.branchCount() : 1,
      billing: period
    };
    if (optionID > 0 && subOptionID > 0 && typeof btoa === 'function') {
      params['options'] = btoa(JSON.stringify({ [optionID]: subOptionID })).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    return params;
  }
  protected price(plan: SubscriptionProduct): string {
    const period = this.period(plan);
    const amount = period === 'P1Y' ? Number(plan.annualPrice || 0) : Number(plan.monthlyPrice || 0);
    if (amount <= 0) return 'Ücretsiz';
    const quantity = plan.branchQuantityEnabled ? this.branchCount() : 1;
    return new Intl.NumberFormat(this.i18n.locale(), { style: 'currency', currency: plan.currency || 'TRY', maximumFractionDigits: 2 }).format(amount * quantity);
  }
  protected priceSuffix(plan: SubscriptionProduct): string {
    const amount = this.period(plan) === 'P1Y' ? Number(plan.annualPrice || 0) : Number(plan.monthlyPrice || 0);
    if (amount <= 0) return 'hemen başlat';
    return this.period(plan) === 'P1Y' ? '/ yıl' : '/ ay';
  }
  private period(plan: SubscriptionProduct): 'P1M' | 'P1Y' {
    const periods = Array.isArray(plan.billingPeriods) ? plan.billingPeriods : [];
    if (this.billing() === 'yearly' && Number(plan.annualPrice || 0) > 0 && periods.includes('P1Y')) return 'P1Y';
    return 'P1M';
  }
}

const SOLUTIONS = [
  { key: 'e-ticaret', title: 'E-ticaret', icon: '◫' },
  { key: 'kuafor', title: 'Kuaför & güzellik', icon: '✦' },
  { key: 'dis-klinigi', title: 'Sağlık sitesi', icon: '✚' },
  { key: 'veteriner-klinigi', title: 'Veteriner', icon: '♧' },
  { key: 'klinik-yonetimi', title: 'Klinik yönetimi', icon: '✙' },
  { key: 'randevu', title: 'Randevu', icon: '◷' },
  { key: 'qr-menu', title: 'QR menü', icon: '▦' },
  { key: 'magaza-pos', title: 'POS & kasa', icon: '▤' },
  { key: 'dijital-buyume', title: 'Sosyal medya', icon: '◎' },
  { key: 'pazaryeri', title: 'Pazaryeri', icon: '◇' },
  { key: 'ozel-platform', title: 'Özel platform', icon: '⌘' }
];
